import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDialog } from '../../context';
import usePortalEditor from '../../hooks/usePortalEditor';
import useMediaQuery from '../../utils/useMediaQuery';
import storyService from '../../services/storyService';
import { getLocation } from '../../services/userLocation';
import BasicsTab from '../profile/editor/BasicsTab';
import VibeTab from '../profile/editor/VibeTab';
import TagsTab from '../profile/editor/TagsTab';
import { REELS_CHANGED } from '../stories/reelRing';

/**
 * EDIT PORTAL PAGE in the portal viewer's shell (Oct 30 2026 handoff): full screen on a phone, a
 * 440px panel on desktop, three of the Portal Editor's tabs on the editor's own state and save
 * (`hooks/usePortalEditor`), so nothing here is forked. The rest of the editor stays at `/portal/edit`.
 *
 * With `onIntroduced`, the footer carries the one new button, INTRODUCE YOURSELF →. It saves first
 * and only then puts the page on the rail: the introduction is a summary of what is saved.
 */
const TABS = [['basics', 'THE BASICS'], ['vibe', 'VIBE + MUSIC'], ['tags', 'STRENGTHS']];
const FOOT_BTN = { height: 48, background: '#161616', boxShadow: 'inset 0 0 0 1px #2A2A2A', color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: '.14em' };

export default function PortalEditSheet({ onClose, onIntroduced = null }) {
  const navigate = useNavigate();
  const { showConfirm } = useDialog();
  const desk = useMediaQuery('(min-width: 900px)');
  const [tab, setTab] = useState('basics');
  const [busy, setBusy] = useState(false);
  const [issue, setIssue] = useState(null);
  const { dto, page, upd, dirty, save, saveState, error, handleState, handleOptions } = usePortalEditor({ onHandleBlocked: () => setTab('basics') });

  const face = dto?.avatarUrl || null;
  const shownError = issue || error;

  const back = async () => {
    if (dirty) {
      const ok = await showConfirm({ title: 'LEAVE WITHOUT SAVING?', message: 'Your changes to this page have not been saved.', confirmLabel: 'LEAVE', cancelLabel: 'STAY', variant: 'danger' });
      if (!ok) return;
    }
    onClose?.();
  };

  const viewPage = async () => {
    if (!(await save())) return;
    onClose?.();
    navigate(`/${page.handle || dto?.handle || ''}`);
  };

  const introduce = async () => {
    if (busy) return;
    setBusy(true);
    setIssue(null);
    if (!(await save())) { setBusy(false); return; }
    const loc = getLocation();
    const r = await storyService.introduce({ lat: loc?.lat ?? null, lng: loc?.lng ?? null });
    setBusy(false);
    if (!r?.success) { setIssue(r?.error || 'That did not go through. Try again.'); return; }
    window.dispatchEvent(new Event(REELS_CHANGED));
    onIntroduced?.();
  };

  const tabProps = { d: page, upd, phone: true };
  const saveLabel = saveState === 'saving' && !busy ? 'SAVING…' : saveState === 'saved' ? 'SAVED' : 'SAVE CHANGES';

  return createPortal(
    <div className="pk pr-overlay-root safe-inset fixed inset-0 z-[100] flex items-center justify-center" data-testid="portal-edit-sheet" role="dialog" aria-modal="true" aria-label="Edit portal page" style={{ background: desk ? 'rgba(0,0,0,.72)' : '#000', color: '#fff' }}>
      <div style={{ display: 'flex', flexDirection: 'column', width: desk ? 440 : '100%', height: desk ? 'min(880px, calc(100% - 48px))' : '100%', borderRadius: desk ? 36 : 0, overflow: 'hidden', background: '#000', boxShadow: '0 0 0 1px #262626' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px 10px' }}>
          <button type="button" onClick={back} aria-label="Back" data-testid="edit-back" className="pk-btn pk-secondary-on-black" style={{ flexShrink: 0, width: 40, height: 40, padding: 0, borderRadius: '50%' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
          </button>
          <span style={{ flex: 1, fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>EDIT PORTAL PAGE</span>
        </div>
        <div role="tablist" className="pk-scroll-x" style={{ display: 'flex', gap: 6, padding: '4px 16px 14px' }}>
          {TABS.map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={k === tab} onClick={() => setTab(k)} className="pk-btn" style={{ flexShrink: 0, height: 36, padding: '0 14px', background: k === tab ? '#fff' : '#161616', color: k === tab ? '#000' : '#fff', fontSize: 9, fontWeight: 800, letterSpacing: '.14em', whiteSpace: 'nowrap' }}>{l}</button>
          ))}
        </div>

        <div data-testid="edit-body" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 16px 20px' }}>
          {page && tab === 'basics' && <BasicsTab {...tabProps} handleState={handleState} handleOptions={handleOptions} />}
          {page && tab === 'vibe' && <VibeTab {...tabProps} />}
          {page && tab === 'tags' && <TagsTab {...tabProps} />}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 16px 18px', borderTop: '1px solid #1F1F1F' }}>
          {shownError && <span role="alert" style={{ fontSize: 12, lineHeight: 1.5, padding: '10px 14px', borderRadius: 16, background: '#2A0B1C', color: '#FFCCE6' }}>{shownError}</span>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button type="button" onClick={save} disabled={!page || busy} data-testid="edit-save" className="pk-btn" style={FOOT_BTN}>{saveLabel}</button>
            <button type="button" onClick={viewPage} disabled={!page || busy} data-testid="edit-view" className="pk-btn" style={FOOT_BTN}>VIEW MY PAGE →</button>
          </div>
          {onIntroduced && (
            <>
              <button type="button" onClick={introduce} disabled={!page || busy} data-testid="edit-introduce" className="pk-btn pk-primary-on-black" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, height: 56, background: '#fff', color: '#000', fontSize: 12, fontWeight: 800, letterSpacing: '.16em' }}>
                {face && <img src={face} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />}
                {busy ? 'SAVING…' : 'INTRODUCE YOURSELF →'}
              </button>
              <span style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, letterSpacing: '.12em', color: '#6B6B6B' }}>SAVES, THEN PUTS YOUR PAGE IN THE RAIL FOR 7 DAYS</span>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
